import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PiStudentLight, PiGraduationCapLight, PiCreditCardLight,
  PiBookOpenLight,
} from "react-icons/pi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import client from "../../api/client";

function StatCard({ icon: Icon, label, value, color, sub, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        {loading
          ? <div className="h-8 w-20 bg-gray-100 rounded animate-pulse mb-1" />
          : <p className="text-3xl font-extrabold text-gray-900 leading-none tabular-nums">{(value ?? 0).toLocaleString()}</p>
        }
        <p className="text-xs font-semibold text-gray-500 mt-1 uppercase tracking-wide">{label}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

const quickLinks = [
  { label: "Students",      path: "/dashboard/admin/students",      desc: "View & export student list",         color: "bg-[#294a93]/10 text-[#294a93]" },
  { label: "Scholarships",  path: "/dashboard/admin/scholarships",  desc: "Manage scholarships & applications", color: "bg-emerald-50 text-emerald-700" },
  { label: "Courses",       path: "/dashboard/admin/courses",       desc: "Courses, cohorts & class schedules", color: "bg-amber-50 text-amber-700" },
  { label: "Subscriptions", path: "/dashboard/admin/subscriptions", desc: "Subscription & payment records",     color: "bg-[#d15424]/10 text-[#d15424]" },
  { label: "Leaderboard",   path: "/dashboard/admin/leaderboard",   desc: "Top performing students",           color: "bg-purple-50 text-purple-700" },
  { label: "Comments",      path: "/dashboard/admin/comments",      desc: "Moderate session comments",         color: "bg-teal-50 text-teal-700" },
];

const CustomSubTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700">{label}</p>
      <p className="text-[#294a93] font-bold mt-0.5">{payload[0].value} subscriptions</p>
    </div>
  );
};

const CustomCohortTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 max-w-[180px]">{label}</p>
      <p className="text-[#d15424] font-bold mt-0.5">{payload[0].value} students</p>
    </div>
  );
};

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/admin/stats")
      .then(res => setStats(res.data))
      .catch(() => toast.error("Failed to load dashboard data."))
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const subChart = stats?.subscriptions_chart ?? [];
  const cohortChart = stats?.cohorts_chart ?? [];

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">

      {/* Page header */}
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-400 mt-0.5">{today}</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={PiStudentLight}
          label="Total Students"
          value={stats?.total_students}
          color="bg-[#294a93]"
          loading={loading}
        />
        <StatCard
          icon={PiCreditCardLight}
          label="Active Subscriptions"
          value={stats?.total_subscriptions}
          color="bg-[#d15424]"
          loading={loading}
        />
        <StatCard
          icon={PiGraduationCapLight}
          label="Scholarship Applications"
          value={stats?.paid_applications}
          sub={`of ${stats?.total_scholarships ?? 0} scholarships`}
          color="bg-emerald-500"
          loading={loading}
        />
        <StatCard
          icon={PiBookOpenLight}
          label="Active Courses"
          value={stats?.total_courses}
          color="bg-amber-500"
          loading={loading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* Subscriptions chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="mb-5">
            <h2 className="font-bold text-gray-900 text-base">Subscriptions</h2>
            <p className="text-xs text-gray-400 mt-0.5">New sign-ups per month (last 6 months)</p>
          </div>
          {loading ? (
            <div className="h-52 bg-gray-50 rounded-xl animate-pulse" />
          ) : subChart.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={subChart} barSize={32} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomSubTooltip />} cursor={{ fill: "#f3f4f6" }} />
                <Bar dataKey="count" fill="#294a93" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Active cohorts chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="mb-5">
            <h2 className="font-bold text-gray-900 text-base">Active Cohorts</h2>
            <p className="text-xs text-gray-400 mt-0.5">Students enrolled per open cohort</p>
          </div>
          {loading ? (
            <div className="h-52 bg-gray-50 rounded-xl animate-pulse" />
          ) : cohortChart.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No active cohorts</div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={cohortChart} barSize={28} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => v.length > 18 ? v.slice(0, 16) + "…" : v}
                />
                <Tooltip content={<CustomCohortTooltip />} cursor={{ fill: "#f3f4f6" }} />
                <Bar dataKey="students" fill="#d15424" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Quick access */}
      <div>
        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Quick Access</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {quickLinks.map(link => (
            <Link key={link.path} to={link.path}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-2 hover:shadow-md hover:border-gray-200 transition-all group">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${link.color}`}>
                {link.label[0]}
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm leading-tight">{link.label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
